import { useState, useRef, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SECTORS } from "@/data/sectors";
import { api, formatApiError, readSse } from "@/lib/api";
import { nativeHeaders, setGuestId } from "@/lib/native";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Send, Sparkles, ShieldCheck, UserRound, Loader2, LifeBuoy, CheckCircle2, FileDown, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import SaveToEvidence from "@/components/SaveToEvidence";

const QUICK_PROMPTS = [
  "Explain the five CQC key questions",
  "What goes in a Statement of Purpose?",
  "Give me a mock inspection checklist for 'Well-led'",
  "How do I prepare for the registered manager interview?",
];

export default function Assistant() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [escOpen, setEscOpen] = useState(false);
  const [escQuestion, setEscQuestion] = useState("");
  const [escEmail, setEscEmail] = useState(user?.email || "");
  const [escLoading, setEscLoading] = useState(false);
  const scrollRef = useRef(null);
  const [params] = useSearchParams();
  const sector = SECTORS[params.get("sector")] ? params.get("sector") : null;
  const prefill = params.get("q");
  useEffect(() => { if (prefill) setInput(prefill); }, [prefill]);
  const [guest, setGuest] = useState(null);
  useEffect(() => {
    if (user) { setGuest(null); return; }
    api.get("/chat/guest-quota").then((r) => { setGuestId(r.data.guest_id); setGuest(r.data); }).catch(() => setGuest({ guest: true, remaining: 3, limit: 3 }));
  }, [user]);
  const locked = !user && guest && guest.remaining <= 0;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/stream`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json", ...nativeHeaders() },
        body: JSON.stringify({ message, session_id: sessionId, sector }),
      });
      if (res.status === 402) {
        setGuest((g) => ({ ...(g || {}), guest: true, remaining: 0 }));
        setMessages((m) => m.slice(0, -1));
        return;
      }
      if (!res.ok || !res.body) {
        let detail;
        try { detail = (await res.json()).detail; } catch { detail = null; }
        throw new Error(formatApiError(detail));
      }
      setMessages((m) => [...m, { role: "assistant", content: "", streaming: true, sponsors: [] }]);
      setLoading(false);
      const patchLast = (fn) => setMessages((m) => { const last = m[m.length - 1]; return [...m.slice(0, -1), { ...last, ...fn(last) }]; });
      let finished = false;
      await readSse(res.body, (event, data) => {
        if (event === "meta") {
          setSessionId(data.session_id);
          patchLast(() => ({ sponsors: data.sponsors || [] }));
          if (data.guest_remaining != null) setGuest((g) => ({ ...(g || {}), guest: true, remaining: data.guest_remaining }));
        } else if (event === "delta") {
          patchLast((last) => ({ content: last.content + data.text }));
        } else if (event === "done") {
          finished = true;
          patchLast(() => ({ content: data.reply, escalate: data.escalate, streaming: false }));
          if (data.escalate) setEscQuestion(message);
        } else if (event === "error") {
          throw new Error(data.detail);
        }
      });
      if (!finished) patchLast(() => ({ streaming: false }));
    } catch (err) {
      toast.error(err.message || "Assistant unavailable");
      setMessages((m) => [...m.filter((x) => !x.streaming), { role: "assistant", content: "Sorry, I couldn't respond just now. Please try again.", error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const submitEscalation = async () => {
    if (!escQuestion.trim() || !escEmail.trim()) return;
    setEscLoading(true);
    try {
      await api.post("/escalations", { question: escQuestion, email: escEmail, session_id: sessionId });
      toast.success("Your question has been sent to a specialist");
      setEscOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send");
    } finally {
      setEscLoading(false);
    }
  };

  const openEscalation = (question) => {
    setEscQuestion(question || "");
    setEscEmail(user?.email || "");
    setEscOpen(true);
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/chat/sessions/${sessionId}/transcript.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "ai-assistant-transcript.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Could not export this conversation"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <span className="overline text-accent">AI Compliance Assistant{sector && <span data-testid="assistant-sector-badge"> · {SECTORS[sector].tag}</span>}</span>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">
          {sector ? `Ask anything about ${SECTORS[sector].tag.split(" · ")[1]} compliance` : "Ask anything about CQC & Ofsted"}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          General guidance only — when a question needs a human, I'll connect you with a specialist.
        </p>
      </div>

      {!user && guest?.guest && !locked && (
        <div className="mb-4 rounded-xl border border-accent/30 bg-accent/10 px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-2" data-testid="guest-quota-banner">
          <span><span className="font-semibold" data-testid="guest-remaining">{guest.remaining}</span> of {guest.limit} free questions left — <Link to="/register" className="text-primary font-semibold hover:underline">create a free account</Link> for unlimited answers, saved chats and specialist escalation.</span>
        </div>
      )}
      {locked && (
        <div className="mb-4 rounded-2xl border border-primary/30 bg-primary/5 p-6 text-center" data-testid="guest-signup-wall">
          <p className="font-display font-bold text-lg mb-1">You've used your 3 free questions</p>
          <p className="text-sm text-muted-foreground mb-4">Create a free account to keep asking — unlimited answers, saved conversations, and a real specialist when the AI can't help.</p>
          <div className="flex justify-center gap-2 flex-wrap">
            <Button asChild data-testid="guest-wall-register"><Link to="/register?next=/assistant">Create free account</Link></Button>
            <Button asChild variant="outline" data-testid="guest-wall-login"><Link to="/login?next=/assistant">Sign in</Link></Button>
          </div>
        </div>
      )}
      <div className="bg-card border border-border rounded-2xl shadow-sm flex flex-col h-[calc(100vh-16rem)] min-h-[420px]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-display font-semibold text-lg mb-1">How can I help today?</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-md">
                Try one of these, or type your own question below.
              </p>
              <div className="grid sm:grid-cols-2 gap-2.5 w-full max-w-lg">
                {QUICK_PROMPTS.map((p) => (
                  <button key={p} onClick={() => send(p)} data-testid="quick-prompt-chip"
                    className="text-left text-sm px-4 py-3 rounded-xl border border-border bg-background hover:border-primary/50 hover:bg-primary/5 transition-colors">
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={m.id || `${m.role}-${i}-${(m.content || "").length}`} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                m.role === "user" ? "bg-primary" : "bg-accent/15"}`}>
                {m.role === "user"
                  ? <UserRound className="w-4 h-4 text-primary-foreground" />
                  : <ShieldCheck className="w-4 h-4 text-accent" />}
              </div>
              <div className={`max-w-[80%] ${m.role === "user" ? "text-right" : ""}`}>
                <div className={`inline-block text-left rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  m.role === "user" ? "bg-primary text-primary-foreground whitespace-pre-wrap" : "bg-secondary text-foreground prose prose-sm prose-neutral dark:prose-invert max-w-none prose-p:my-1.5 prose-headings:my-2 prose-headings:text-base prose-ul:my-1.5 prose-li:my-0.5 prose-hr:my-3"}`}>
                  {m.role === "user" ? m.content : <ReactMarkdown>{m.content}</ReactMarkdown>}
                  {m.streaming && <span className="inline-block w-2 h-4 ml-0.5 align-text-bottom bg-accent/70 animate-pulse rounded-sm" data-testid="streaming-cursor" />}
                </div>
                {m.sponsors?.length > 0 && (
                  <div className="mt-2 space-y-1.5" data-testid="sponsored-partners">
                    {m.sponsors.map((sp) => (
                      <a key={sp.id} href={sp.url} target="_blank" rel="noopener noreferrer sponsored" onClick={() => api.post(`/sponsors/${sp.id}/click`).catch(() => {})}
                        className="flex items-start gap-2 rounded-xl border border-amber-400/40 bg-amber-50 dark:bg-amber-500/10 px-3 py-2.5 hover:border-amber-400 transition-colors"
                        data-testid={`sponsor-card-${sp.id}`}>
                        <Megaphone className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <span className="text-xs flex-1">
                          <span className="block font-semibold">{sp.company} <span className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300 ml-1">Sponsored · {sp.category}</span></span>
                          <span className="text-muted-foreground">{sp.description}</span>
                        </span>
                      </a>
                    ))}
                  </div>
                )}
                {m.escalate && (
                  <div className="mt-2 flex items-center gap-2 rounded-xl border border-accent/40 bg-accent/5 px-3 py-2.5" data-testid="escalation-banner">
                    <LifeBuoy className="w-4 h-4 text-accent shrink-0" />
                    <span className="text-xs text-muted-foreground flex-1">This needs a specialist.</span>
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => openEscalation(messages[i - 1]?.content)} data-testid="ai-escalate-human-button">
                      Connect me
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent/15 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-4 h-4 text-accent" />
              </div>
              <div className="bg-secondary rounded-2xl px-4 py-3">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-border p-3 sm:p-4">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about CQC, Ofsted, registration, policies…"
              rows={1}
              data-testid="ai-chat-input"
              disabled={locked}
              className="resize-none min-h-[44px] max-h-32"
            />
            <Button type="submit" disabled={loading || locked || !input.trim()} data-testid="ai-chat-submit-button" className="h-11 px-4">
              <Send className="w-4 h-4" />
            </Button>
          </form>
          <div className="flex items-center justify-between mt-2 px-1">
            <div className="flex items-center gap-3">
              <p className="text-xs text-muted-foreground">Press Enter to send · <span data-testid="assistant-privacy-notice">Don't include patient, child or staff identifiable details — use roles or initials.</span></p>
              {sessionId && (
                <>
                  <a href="#export" onClick={(e) => { e.preventDefault(); exportPdf(); }} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="chat-export-pdf-button">
                    <FileDown className="w-3.5 h-3.5" /> Export as PDF
                  </a>
                  <SaveToEvidence sourceType="transcript" refId={sessionId} defaultTitle="AI assistant transcript" variant="ghost" size="sm" />
                </>
              )}
            </div>
            <button onClick={() => openEscalation("")} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="manual-escalate-button">
              <LifeBuoy className="w-3.5 h-3.5" /> Ask a human specialist
            </button>
          </div>
        </div>
      </div>

      <Dialog open={escOpen} onOpenChange={setEscOpen}>
        <DialogContent data-testid="escalation-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Connect with a specialist</DialogTitle>
            <DialogDescription>
              We'll review your question and get back to you directly by email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="esc-q">Your question</Label>
              <Textarea id="esc-q" value={escQuestion} onChange={(e) => setEscQuestion(e.target.value)}
                rows={4} className="mt-1.5" data-testid="escalation-question-input"
                placeholder="Describe what you need help with…" />
            </div>
            <div>
              <Label htmlFor="esc-e">Reply-to email</Label>
              <Input id="esc-e" type="email" value={escEmail} onChange={(e) => setEscEmail(e.target.value)}
                className="mt-1.5" data-testid="escalation-email-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEscOpen(false)}>Cancel</Button>
            <Button onClick={submitEscalation} disabled={escLoading} data-testid="escalation-submit-button">
              {escLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Send to specialist</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
